import _ from "lodash";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { mergeClass, pause, removeTags } from "../../utils";
import { useEffect, useState } from "react";
import Select from "react-select";
import { isVideo } from "../Upload";
import { getStillVideoFrameFromUrl, uploadFile } from "./FileHandling/Utils";
import { tMsg } from "../ToastNotifications";
import { Spinner } from "../Loader";
import { useAtomValue } from "jotai";
import { hostDataAtom, useModifyUploadList } from "../../atoms";
import { IDropdownOption, dropdownFields } from "../../utils/customisation";
import { Button } from "../Button";
import {
  BaseStringArray,
  StringArrayItem,
  TextInputForStringArray,
} from "./stringArrayComponents";
import { fieldId } from "./utils";
import { FileUpload } from "./FileHandling/FileUpload";

interface IBaseInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: any;
  name: string;
  type: keyof typeof FieldList | string;
  modelname?: string;
  onChange: (value: any) => void;
}

interface IDropdownProps extends IBaseInputProps {
  options?: IDropdownOption[];
}

const FieldName = ({
  name,
  hint,
  required,
}: {
  name: string;
  hint?: string | React.ReactNode;
  required?: boolean;
}): JSX.Element => (
  <div>
    <div className="font-medium">
      {name}
      {required && <span className="text-red-500"> *</span>}
    </div>
    {hint && <div className="text-xs opacity-60">{hint}</div>}
  </div>
);

const TextOrNumber = ({
  name,
  value,
  ...restProps
}: IBaseInputProps): JSX.Element => (
  <input
    id={fieldId(name)}
    value={value || ""}
    className="w-full border border-[#ccc] p-2.5 text-sm"
    {...restProps}
  />
);

const StringArray = (props: IBaseInputProps): JSX.Element => (
  <BaseStringArray {...props} />
);

const Checkbox = (props: IBaseInputProps): JSX.Element => {
  const { name, value, onChange } = props;
  const checkboxVal = _.isBoolean(value) && value === true;
  return (
    <div className="order-first grid content-center">
      <input
        type="checkbox"
        className="h-5 w-5"
        id={fieldId(name)}
        checked={checkboxVal}
        onChange={(e) => onChange(e)}
      />
    </div>
  );
};

const RichTextInput = (props: IBaseInputProps): JSX.Element => {
  const countWords = (text: any) => {
    if (_.isString(text)) {
      const addSpacesToParas = text.replaceAll("</p><p>", "</p> <p>");
      const cleanHtml = addSpacesToParas.replaceAll(/<[^>]+>/g, "");
      return _.words(cleanHtml).length;
    } else if (_.isNumber(text)) return _.words(text.toString()).length;
    else return 0;
  };
  const { name, value, onChange } = props;
  const [wordCount, setWordCount] = useState<number>(countWords(value));

  const modules = {
    toolbar: [["bold", "italic"], ["clean"]],
  };
  const getValue = _.isString(value) ? value : "";

  // Attribute fonts
  const Font = ReactQuill.Quill.import("attributors/class/font");
  Font.whitelist = ["Manrope"];
  ReactQuill.Quill.register(Font, true);

  // FIX: ofApp only accepts <i> tag for italics, but quill uses <em> by default, this fixes that
  // Source: https://github.com/KillerCodeMonkey/ngx-quill/issues/101#issuecomment-358270459
  const Inline = ReactQuill.Quill.import("blots/inline");
  class ItalicBlot extends Inline {}
  ItalicBlot.blotName = "italic";
  ItalicBlot.tagName = "i";
  ReactQuill.Quill.register(ItalicBlot);

  return (
    <>
      <div className="w-full h-36">
        <ReactQuill
          id={fieldId(name)}
          className="w-full h-24"
          theme="snow"
          onChange={(e) => {
            setWordCount(countWords(e));
            const withoutTags = _.trim(removeTags(e));
            const value = withoutTags.length < 1 ? "" : e;
            const obj = { target: { value } };
            if (value !== getValue) onChange(obj);
          }}
          modules={modules}
          value={getValue}
        />
      </div>
      <div className="text-xs">Words: {wordCount}</div>
    </>
  );
};

const Dropdown = (props: IDropdownProps): JSX.Element => {
  const {
    name,
    options,
    className,
    value,
    onChange,
    modelname,
    required,
    disabled,
  } = props;
  const useOptions =
    options ||
    (modelname &&
      dropdownFields.find((d) => d.model === modelname && d.field === name)
        ?.options) ||
    [];
  const dropdownValue = useOptions?.find((o) => o.value === value) || null;
  return (
    <Select
      name={name}
      options={useOptions}
      value={dropdownValue}
      className={className}
      onChange={(e) => {
        // console.log(e);
        const obj = { target: { value: e?.value } };
        onChange(obj);
      }}
      required={required}
      isDisabled={disabled}
    />
  );
};

const VideoUpload = (props: IBaseInputProps) => (
  <FileUpload {...props} accept={props.accept || "video/mp4"} />
);
const ImageUpload = (props: IBaseInputProps) => (
  <FileUpload {...props} accept={props.accept || "image/jpeg, image/png"} />
);
const OtherUpload = (props: IBaseInputProps) => (
  <FileUpload {...props} nothumb />
);

const FieldList = {
  number: TextOrNumber,
  string: TextOrNumber,
  string_100: TextOrNumber,
  string_200: TextOrNumber,
  string_400: TextOrNumber,
  bool: Checkbox,
  wysiwyg: RichTextInput,
  file: OtherUpload,
  image: ImageUpload,
  video: VideoUpload,
  dropdown: Dropdown,
  string_array: StringArray,
};

const DefaultFieldList = {
  number: null,
  string: "",
  boolean: null,
  wysiwyg: "",
  file: null,
  image: "",
  video: "",
  dropdown: null,
};

const isValidFieldType = (k: string): k is keyof typeof FieldList => {
  return k in FieldList;
};

export { FieldName, FieldList, DefaultFieldList, isValidFieldType, Dropdown };
export type { IBaseInputProps, IDropdownOption };
